// Block Company Listing (Figma node 384:8975): Division + Location filter pills, the expand-in-
// row company info panel, sticky scroll-position dots, and hash deep-linking from the site nav's
// Companies sub-menu (which links to "#<company-url-segment>" - see
// DataCompanyEntityController.cs). Everything renders server-side up front (the full company
// tree, no properties on the block itself - see BlockCompanyListingViewComponent); this only
// filters/toggles/observes what's already in the DOM, no fetch involved.
//
// Follows main.ts's data-* init convention: one class instance per [data-company-listing] root.

import { trackEngageEvent } from "../analytics/engage-events";
import { countText, formatText } from "../i18n/count-text";
import { whenComponentsCssApplied } from "../startup";
import { swapPillVisibility } from "./pill-swap";
import { bindScrollRow } from "./scroll-row-wheel";

interface ActiveFilters {
    division: Set<string>;
    continent: Set<string>;
    country: Set<string>;
}

const FILTER_GROUPS: Array<keyof ActiveFilters> = ["division", "continent", "country"];

// Continent and Country together are the Location row: it starts with just the continents, and
// selecting one hides the others and shows its countries instead, to narrow it down - one
// continent at a time, any number of its countries (see matchesLocationFilter). Deselecting it
// brings the continents back. Without continents (none in DATA/Geography) the row has only the
// countries, every one shown.
const LOCATION_GROUPS: Array<keyof ActiveFilters> = ["continent", "country"];

// A tile and its panel, in the server's order.
interface GridEntry {
    item: HTMLButtonElement;
    panel: HTMLElement | null;
}

// One category's grid, with its tiles in their original order (arrangeGrid reorders the DOM).
interface Grid {
    element: HTMLElement;
    columns: number;
    entries: GridEntry[];
}

class CompanyListing {
    private readonly root: HTMLElement;
    private readonly items: HTMLButtonElement[];
    private readonly categories: HTMLElement[];
    private readonly divisions: HTMLElement[];
    private readonly filterPills: HTMLButtonElement[];
    // The Location row's pills (continents, then countries) - see updateLocationPillVisibility.
    private readonly locationPillRow: HTMLElement | null;
    // The line between the selected continent and its countries (only with continents).
    private readonly locationDivider: HTMLElement | null;
    private readonly statusElement: HTMLElement | null;
    private readonly grids: Grid[];
    // Each group's pill values by their lower-case form - what a ?division=/?continent=/?country=
    // value from the URL is matched against (see readFiltersFromUrl).
    private readonly pillValues: Record<keyof ActiveFilters, Map<string, string>> = { division: new Map(), continent: new Map(), country: new Map() };
    // Each country pill's continent (its data-continent), by country code. Assumes a country sits
    // under one continent in DATA/Geography: a code under two would only count toward the last.
    private readonly countryContinents = new Map<string, string>();
    // Re-checks whether the Location row overflows (scroll-row-wheel.ts) - hiding pills shrinks
    // its content without resizing the row, so its ResizeObserver doesn't notice.
    private updateLocationRowScrollable: () => void = () => {};
    // Pending chase start per dots row (setRowActive), cancelled when the row leaves the viewport.
    private readonly chaseTimers = new Map<HTMLElement, number>();

    private readonly active: ActiveFilters = { division: new Set(), continent: new Set(), country: new Set() };

    constructor(root: HTMLElement) {
        this.root = root;
        this.items = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-company-listing-item]"));
        this.categories = Array.from(root.querySelectorAll<HTMLElement>("[data-company-listing-category]"));
        this.divisions = Array.from(root.querySelectorAll<HTMLElement>("[data-company-listing-division]"));
        this.filterPills = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-company-listing-filter]"));
        this.locationPillRow = root.querySelector<HTMLElement>("[data-company-listing-filter-group='location'] .block-company-listing__filter-pills");
        this.locationDivider = root.querySelector<HTMLElement>("[data-company-listing-location-divider]");
        this.statusElement = root.querySelector<HTMLElement>("[data-company-listing-status]");
        this.grids = Array.from(root.querySelectorAll<HTMLElement>("[data-company-listing-grid]")).map((element) => ({
            element,
            columns: Number(element.dataset.columns) || 3,
            entries: Array.from(element.querySelectorAll<HTMLButtonElement>("[data-company-listing-item]")).map((item) => ({
                item,
                panel: this.panelOf(item),
            })),
        }));

        this.initFilters();
        this.initFilterRowWheel();
        this.initItemToggles();
        this.initResetLinks();
        this.initDotsVisibility();

        this.readFiltersFromUrl();
        this.applyFilters();
        this.initHashLinking();
    }

    // ---- Filters ----

    private initFilters(): void {
        this.filterPills.forEach((pill) => {
            const pillGroup = pill.dataset.companyListingFilter as keyof ActiveFilters | undefined;
            if (pillGroup && pill.dataset.value) {
                this.pillValues[pillGroup].set(pill.dataset.value.toLowerCase(), pill.dataset.value);
                if (pillGroup === "country" && pill.dataset.continent) {
                    this.countryContinents.set(pill.dataset.value, pill.dataset.continent);
                }
            }

            pill.addEventListener("click", () => {
                const group = pill.dataset.companyListingFilter as keyof ActiveFilters | undefined;
                const value = pill.dataset.value;
                if (!group || !value) {
                    return;
                }

                const set = this.active[group];
                if (set.has(value)) {
                    set.delete(value);
                } else {
                    // One continent at a time - the others aren't even shown while one is selected.
                    if (group === "continent") {
                        set.clear();
                    }
                    set.add(value);
                }

                if (group === "continent") {
                    this.pruneCountries();
                    // Back to the start of the (mobile) scroll row, where the selected continent
                    // and its first countries - or the continents again - now are.
                    if (this.locationPillRow) {
                        this.locationPillRow.scrollLeft = 0;
                    }
                }
                this.syncPillPressedStates();
                this.syncUrl("push");
                this.applyFilters(true);
                this.announceResult();
            });
        });

        window.addEventListener("popstate", () => {
            this.readFiltersFromUrl();
            this.applyFilters();
            this.announceResult();
        });
    }

    // Says how many companies the filters leave, in the polite live region
    // (BlockCompanyListing.cshtml) - after a visitor's own filter change only, not on load or when
    // a deep link clears the filters, where nothing has changed from their point of view.
    private announceResult(): void {
        if (this.statusElement) {
            const shown = this.items.filter((item) => !item.hidden).length;
            const template = (shown === 1 ? this.statusElement.dataset.textOne : this.statusElement.dataset.textOther) ?? "{0} / {1}";
            this.statusElement.textContent = formatText(template, shown, this.items.length);
        }
    }

    // Mouse wheel + drag-to-scroll for the pill row that scrolls sideways on mobile widths (the
    // Location row - see block-company-listing.css and scroll-row-wheel.ts).
    private initFilterRowWheel(): void {
        this.root.querySelectorAll<HTMLElement>(".block-company-listing__filter-pills.scroll-row").forEach((row) => {
            const updateScrollable = bindScrollRow(row);
            if (row === this.locationPillRow) {
                this.updateLocationRowScrollable = updateScrollable;
            }
        });
    }

    // Same button/pill/pill--toggle markup as the year filter in press releases (see
    // atoms/pill.css's own comment on .pill--toggle) - aria-pressed is the accessible state,
    // data-active is what atoms/pill.css's [data-active="true"] selector reads to paint the
    // selected fill. button-hover.ts's own hover slide-fill is separate from both (keyed off the
    // shared "button" class instead), so it works on these pills automatically once that class
    // is present in the markup, with no wiring needed here.
    private setPillPressedState(pill: HTMLButtonElement, pressed: boolean): void {
        pill.setAttribute("aria-pressed", String(pressed));
        pill.dataset.active = String(pressed);
    }

    // Every pill's pressed state from the active filters.
    private syncPillPressedStates(): void {
        this.filterPills.forEach((pill) => {
            const group = pill.dataset.companyListingFilter as keyof ActiveFilters | undefined;
            const value = pill.dataset.value;
            if (group && value) {
                this.setPillPressedState(pill, this.active[group].has(value));
            }
        });
    }

    // Only values a pill has count, matched without regard to case (?country=se is SE): a
    // division id from another environment or a country no company has would otherwise filter
    // everything away with no pill pressed to show why. Only the first continent counts (one at a
    // time - BlockCompanyListing.cshtml renders the same one), and a country outside it (or with
    // none selected) is dropped too (pruneCountries) - its pill isn't even shown. Whatever was
    // dropped is corrected in the URL in place, so it never claims a filter that isn't applied.
    private readFiltersFromUrl(): void {
        const params = new URL(window.location.href).searchParams;
        let droppedContinent = false;

        FILTER_GROUPS.forEach((group) => {
            const set = this.active[group];
            set.clear();
            params.getAll(group).forEach((value) => {
                const pillValue = this.pillValues[group].get(value.toLowerCase());
                if (!pillValue) {
                    return;
                }
                if (group === "continent" && set.size > 0) {
                    droppedContinent = true;
                } else {
                    set.add(pillValue);
                }
            });
        });

        if (this.pruneCountries() || droppedContinent) {
            this.syncUrl("replace");
        }
        this.syncPillPressedStates();
    }

    // Whether the Location row has continents at all (see LOCATION_GROUPS).
    private hasContinentFilter(): boolean {
        return this.pillValues.continent.size > 0;
    }

    // Whether a country's pill is offered: it belongs to the selected continent - or, without
    // continents, always.
    private isCountryOffered(code: string): boolean {
        if (!this.hasContinentFilter()) {
            return true;
        }
        const continent = this.countryContinents.get(code);
        return continent !== undefined && this.active.continent.has(continent);
    }

    // Whether a continent's pill is offered: while none is selected, or it's the selected one.
    private isContinentOffered(code: string): boolean {
        return this.active.continent.size === 0 || this.active.continent.has(code);
    }

    // Drops selected countries that aren't offered any more (deselecting Europe releases Sweden),
    // so no hidden country pill keeps filtering. Only the active set - the pills follow via
    // syncPillPressedStates. Returns whether any was dropped.
    private pruneCountries(): boolean {
        let pruned = false;
        this.active.country.forEach((code) => {
            if (!this.isCountryOffered(code)) {
                this.active.country.delete(code);
                pruned = true;
            }
        });
        return pruned;
    }

    // The Location row's pills: the continents until one is selected, then that one followed by
    // its countries (every country, without continents). BlockCompanyListing.cshtml renders the
    // URL's state already, so nothing pops in on load. animate: a visitor's own change, where the
    // countries (and the divider) slide up into place while the continents just hide and show
    // (pill-swap.ts) - not on load, Back or a deep link.
    private updateLocationPillVisibility(animate: boolean): void {
        const hiddenStates = new Map<HTMLElement, boolean>();
        this.filterPills.forEach((pill) => {
            const value = pill.dataset.value;
            if (!value) {
                return;
            }
            if (pill.dataset.companyListingFilter === "continent") {
                hiddenStates.set(pill, !this.isContinentOffered(value));
            } else if (pill.dataset.companyListingFilter === "country") {
                hiddenStates.set(pill, !this.isCountryOffered(value));
            }
        });
        // Swapped like a pill, so it's revealed with the countries.
        if (this.locationDivider) {
            hiddenStates.set(this.locationDivider, this.active.continent.size === 0);
        }

        if (animate && this.locationPillRow) {
            // Once more when the motion is done: the leaving pills' copies take up room until then.
            const animated = (pill: HTMLElement): boolean => pill.dataset.companyListingFilter !== "continent";
            void swapPillVisibility(this.locationPillRow, hiddenStates, animated).then(() => this.updateLocationRowScrollable());
        } else {
            hiddenStates.forEach((hidden, pill) => {
                pill.hidden = hidden;
            });
        }
        this.updateLocationRowScrollable();
    }

    // "push" for a visitor's own filter change (Back undoes it); "replace" when the page changes
    // the filters itself, e.g. to show a deep-linked company on load - no extra history entry.
    private syncUrl(history: "push" | "replace"): void {
        const url = new URL(window.location.href);
        FILTER_GROUPS.forEach((group) => {
            url.searchParams.delete(group);
            this.active[group].forEach((value) => url.searchParams.append(group, value));
        });
        if (history === "push") {
            window.history.pushState({}, "", url);
        } else {
            window.history.replaceState(window.history.state, "", url);
        }
    }

    // groups defaults to every filter (used by the hash-deep-link fallback, which needs to
    // guarantee the linked company becomes visible regardless of which filter was hiding it) -
    // the empty-state "Clear location filter" button instead passes just LOCATION_GROUPS, since
    // a location filter emptying a category should never also discard an unrelated Division
    // selection the user made deliberately.
    private clearFilters(groups: ReadonlyArray<keyof ActiveFilters> = FILTER_GROUPS, history: "push" | "replace" = "push"): void {
        groups.forEach((group) => this.active[group].clear());
        this.syncPillPressedStates();
        this.syncUrl(history);
        this.applyFilters(history === "push");
    }

    private matchesDivisionFilter(item: HTMLButtonElement): boolean {
        const division = item.dataset.division ?? "";
        return this.active.division.size === 0 || this.active.division.has(division);
    }

    // Selected countries decide when there are any - they're always within the selected continent
    // (pruneCountries), and without continents they're all there is. Otherwise a selected
    // continent matches a company with any of its countries in it, the same "countries" the map's
    // continent counts use. Nothing selected matches every company.
    private matchesLocationFilter(item: HTMLButtonElement): boolean {
        const countries = (item.dataset.country ?? "").split(" ").filter(Boolean);
        if (this.active.country.size > 0) {
            return countries.some((code) => this.active.country.has(code));
        }
        if (this.active.continent.size > 0) {
            return countries.some((code) => this.active.continent.has(this.countryContinents.get(code) ?? ""));
        }
        return true;
    }

    private matchesFilters(item: HTMLButtonElement): boolean {
        return this.matchesDivisionFilter(item) && this.matchesLocationFilter(item);
    }

    // animate: see updateLocationPillVisibility.
    private applyFilters(animate = false): void {
        this.updateLocationPillVisibility(animate);
        this.items.forEach((item) => {
            item.hidden = !this.matchesFilters(item);
            // A filtered-out company's panel doesn't stay open under a row it's no longer in.
            if (item.hidden && item.getAttribute("aria-expanded") === "true") {
                this.closeAllPanels();
            }
        });

        this.grids.forEach((grid) => {
            arrangeGrid(grid);
            updateGridLines(grid);
        });
        this.categories.forEach((category) => this.updateCategoryEmptyState(category));
        this.updateDivisionVisibility();
        this.rebuildDots();
    }

    // A category's own grid + empty-state message toggle together: the grid hides and the
    // message shows whenever every item within it is filtered out - by EITHER filter. The
    // message wording distinguishes why, though: a Division filter emptying a category also
    // empties (and hides) its whole division via updateDivisionVisibility below, so no message
    // is needed there - but a location filter emptying a category must NOT hide the division
    // (see that method's own comment for why), so this is the only visible sign of it, and needs
    // to name the location filter specifically as the thing to clear, with a count of how many
    // companies it's hiding (not just "no companies match").
    private updateCategoryEmptyState(category: HTMLElement): void {
        const grid = category.querySelector<HTMLElement>("[data-company-listing-grid]");
        const emptyState = category.querySelector<HTMLElement>("[data-company-listing-empty-state]");
        const emptyStateText = category.querySelector<HTMLElement>("[data-company-listing-empty-state-text]");
        if (!grid || !emptyState) {
            return;
        }

        const items = Array.from(category.querySelectorAll<HTMLButtonElement>("[data-company-listing-item]"));
        const hasVisibleItem = items.some((item) => !item.hidden);

        grid.hidden = !hasVisibleItem;
        emptyState.hidden = hasVisibleItem;

        if (!hasVisibleItem && emptyStateText) {
            // Every item here already fails matchesFilters (since hasVisibleItem is false) - if
            // any of them WOULD pass the division filter alone, the location filter is what's
            // actually hiding them, and the category only looks empty because of that (its
            // division stays visible, per updateDivisionVisibility). Otherwise the division
            // filter itself excludes this category outright, matching the division's own hidden
            // state, so no message is needed - a hidden division already explains the emptiness.
            const hiddenByLocationOnly = items.filter((item) => this.matchesDivisionFilter(item) && !this.matchesLocationFilter(item));

            emptyStateText.textContent = hiddenByLocationOnly.length > 0
                ? countText(emptyState, hiddenByLocationOnly.length)
                : "";

            const resetButton = emptyState.querySelector<HTMLButtonElement>("[data-company-listing-reset-location-filter]");
            if (resetButton) {
                resetButton.hidden = hiddenByLocationOnly.length === 0;
            }
        }
    }

    // A division section (heading/description/grid) hides entirely once the DIVISION filter
    // alone excludes every one of its companies - deliberately NOT considering the location
    // filter here, per the user: only the Division pills should ever be able to hide a whole
    // division. A location filter that happens to leave a category empty still hides that
    // category's own grid (see updateCategoryEmptyState above, hasVisibleItem is filter-agnostic)
    // but the division itself, and its other categories, stay visible and reachable.
    private updateDivisionVisibility(): void {
        this.divisions.forEach((division) => {
            const items = Array.from(division.querySelectorAll<HTMLButtonElement>("[data-company-listing-item]"));
            const hasDivisionMatch = items.some((item) => this.matchesDivisionFilter(item));
            division.hidden = !hasDivisionMatch;
        });
    }

    private initResetLinks(): void {
        this.root.querySelectorAll<HTMLButtonElement>("[data-company-listing-reset-location-filter]").forEach((button) => {
            button.addEventListener("click", () => {
                this.clearFilters(LOCATION_GROUPS);
                this.announceResult();
            });
        });
    }

    // ---- Expand/collapse ----

    private initItemToggles(): void {
        this.items.forEach((item) => {
            item.addEventListener("click", () => {
                if (this.toggleItem(item)) {
                    trackCompanyOpen(item);
                }
            });
        });
    }

    private panelOf(item: HTMLButtonElement): HTMLElement | null {
        const panelId = item.getAttribute("aria-controls");
        return panelId ? this.root.querySelector<HTMLElement>(`#${CSS.escape(panelId)}`) : null;
    }

    // Returns whether the panel opened (false when it closed or wasn't found).
    private toggleItem(item: HTMLButtonElement): boolean {
        const panel = this.panelOf(item);
        if (!panel) {
            return false;
        }

        const willOpen = panel.dataset.open !== "true";
        this.closeAllPanels();

        if (willOpen) {
            panel.dataset.open = "true";
            item.setAttribute("aria-expanded", "true");
        }

        return willOpen;
    }

    // A deep link only ever opens: following one to a company whose panel is already open must
    // not close it. Returns whether it opened now.
    private openItem(item: HTMLButtonElement): boolean {
        return this.panelOf(item)?.dataset.open !== "true" && this.toggleItem(item);
    }

    // data-open, not [hidden] - the panel stays in normal flow throughout so
    // block-company-listing.css's grid-template-rows transition has something to animate; [hidden]
    // would jump straight to display:none with no transition to play (see that CSS rule's comment).
    private closeAllPanels(): void {
        this.root.querySelectorAll<HTMLElement>("[data-company-listing-panel]").forEach((panel) => {
            panel.dataset.open = "false";
        });
        this.items.forEach((item) => item.setAttribute("aria-expanded", "false"));
    }

    // ---- Per-division scroll-progress dots ----
    // Every division has its own [data-company-listing-dots] row (see BlockCompanyListing.cshtml
    // - sitting right after that division's own description inside .division-aside-sticky,
    // matching Figma's per-division placement), one dot per division overall. Which dot is
    // active WITHIN a row is STATIC, server-rendered (dot N is active in section N's own row,
    // matching that division's own position among the others) - never scroll-tracked.
    //
    // Each row reveals as soon as the row itself comes into view (from the bottom while scrolling
    // down - per the user, on both breakpoints), and resets once it has fully left, so it plays
    // again next time. It used to reveal only when its DIVISION became the most visible one,
    // which on mobile (dots now at the top of the aside) and desktop (dots at the bottom of the
    // 750px sticky column) meant the dots were already on screen, blank, well before revealing.
    private initDotsVisibility(): void {
        const rows = Array.from(this.root.querySelectorAll<HTMLElement>("[data-company-listing-dots]"));

        if (!("IntersectionObserver" in window)) {
            // No observer support: show immediately - a missing entrance animation beats dots
            // that never appear at all.
            rows.forEach((dots) => this.setRowActive(dots, true));
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => this.setRowActive(entry.target as HTMLElement, entry.isIntersecting));
        });
        rows.forEach((dots) => observer.observe(dots));
    }

    // Tracks whether the row is in view (data-row-active) and, once it comes into view, starts
    // the "chase" light effect a moment later (CHASE_START_DELAY_MS): a knight-rider-style light that always
    // starts from dot 0 and runs through every dot in sequence up to this row's own target dot
    // (its STATIC data-active dot, never scroll-tracked - see rebuildDots's own comment on why
    // division identity, not scroll position, decides this), each one lighting briefly then
    // fading back out except the target, which stays lit (see block-company-listing.css's
    // @keyframes company-listing-dot-chase / [data-chasing]/[data-swept] rules for how that's
    // rendered).
    //
    // Only triggers the chase on an actual false -> true transition (the row coming into view),
    // not on every observer callback, so it plays once per entry rather than continuously while
    // scrolling.
    private setRowActive(dots: HTMLElement, isActive: boolean): void {
        const wasActive = dots.dataset.rowActive === "true";
        dots.dataset.rowActive = String(isActive);

        if (isActive && !wasActive) {
            this.chaseTimers.set(dots, window.setTimeout(() => {
                this.chaseTimers.delete(dots);
                this.runDotChase(dots);
            }, CompanyListing.CHASE_START_DELAY_MS));
        } else if (!isActive && wasActive) {
            // A row scrolled past before its chase started: cancelled, or it would set data-swept
            // after the reset below and the next entry would find the target already lit.
            window.clearTimeout(this.chaseTimers.get(dots));
            this.chaseTimers.delete(dots);
            // Resets every dot's data-swept/data-in-chase-path back to false as soon as this row
            // stops being active - without this, scrolling away and back to the SAME division
            // re-sets the exact same target dot's data-swept to "true" it already had from last
            // time (division N always maps to the same static dot N - never scroll-tracked, see
            // rebuildDots's own comment on why), which is not a value CHANGE at all, so no CSS
            // transition/animation ever fires and the target dot just snaps straight to its lit
            // state instead of re-running the chase (confirmed live - this is exactly why
            // scrolling away and back made the target look "stuck" already lit from the start).
            dots.querySelectorAll<HTMLElement>("[data-company-listing-dot]").forEach((dot) => {
                dot.dataset.swept = "false";
                dot.dataset.inChasePath = "false";
            });
        }
    }

    // How long after the row comes into view the chase starts. It used to wait for the dots' own
    // reveal-in (since removed - see block-company-listing.css); the pause stayed, so the chase
    // plays once the row is on screen rather than as it slides in at the edge.
    private static readonly CHASE_START_DELAY_MS = 450;

    // The chase's timing, matching block-company-listing.css: each dot starts CHASE_STEP_MS after
    // the one before it (--chase-index) and lights for CHASE_KEYFRAME_MS. data-chasing is cleared
    // once the last one has finished, so a later, unrelated style recalc can't replay the keyframe
    // animation on a dot that isn't mid-chase (CSS keyframe animations restart from a fresh
    // [data-chasing="true"] match, not just its first one).
    private static readonly CHASE_STEP_MS = 100;
    private static readonly CHASE_KEYFRAME_MS = 200;

    private runDotChase(dots: HTMLElement): void {
        // Only the dots still shown: those of divisions the Division filter hides don't take a
        // step in the chase. Their index is the --chase-index the CSS delays are computed from, so
        // any number of divisions works.
        const dotEls = Array.from(dots.querySelectorAll<HTMLElement>("[data-company-listing-dot]")).filter((dot) => !dot.hidden);
        const activeIndex = dotEls.findIndex((dot) => dot.dataset.active === "true");

        // data-swept marks the target dot itself (stays lit, never animates). data-in-chase-path
        // marks every dot from 0 up to and including the target (index <= activeIndex) - the
        // chase's own CSS animation is scoped to THIS, not to "every dot that isn't the target",
        // so a dot AFTER the target (index > activeIndex) never lights up at all rather than
        // running the full chase the whole way to the end of the row regardless of where the
        // target actually sits (confirmed live: without this, a target at index 1 still visibly
        // ran the chase through dots 2 and 3 too, then jumped back to dot 1 and stayed lit there
        // - the chase has to stop exactly at the target, not run past it and snap back).
        dotEls.forEach((dot, index) => {
            dot.style.setProperty("--chase-index", String(index));
            dot.dataset.swept = String(index === activeIndex);
            dot.dataset.inChasePath = String(activeIndex >= 0 && index <= activeIndex);
        });

        dots.dataset.chasing = "true";
        window.setTimeout(() => {
            dots.dataset.chasing = "false";
        }, Math.max(activeIndex, 0) * CompanyListing.CHASE_STEP_MS + CompanyListing.CHASE_KEYFRAME_MS);
    }

    // Dot count reflects divisions still visible after filtering, not always the full 4 -
    // deliberately keyed off division.hidden alone (set by updateDivisionVisibility, which is
    // itself deliberately Division-filter-only, per the user) - so a location filter, however
    // many companies it hides, never changes the dot count. Only the Division pills can. Each
    // dot's active/inactive fill never changes here - only whether it's shown at all.
    private rebuildDots(): void {
        const visibleDivisionIds = this.divisions.filter((division) => !division.hidden).map((division) => division.dataset.companyListingDivision);

        this.root.querySelectorAll<HTMLElement>("[data-company-listing-dot]").forEach((dot) => {
            dot.hidden = !visibleDivisionIds.includes(dot.dataset.companyListingDot);
        });
    }

    // ---- Hash deep-linking ----
    // "#<company-url-segment>" opens that company's panel (the nav's company links - see
    // DataCompanyEntityController); a division's or category's anchor (see CompanyAnchors.cs, the
    // nav's division heading and DataCompanyDivisionController/DataCompanyCategoryController)
    // scrolls to that section instead.

    private initHashLinking(): void {
        window.addEventListener("hashchange", () => this.openFromHash());
        this.openFromHash();
    }

    private openFromHash(): void {
        const id = window.location.hash.slice(1);
        if (!id) {
            return;
        }

        const item = this.items.find((candidate) => candidate.id === id);
        if (!item) {
            this.scrollToSection(id);
            return;
        }

        // A filter that currently hides the linked company would otherwise make the link a
        // silent no-op - clearing filters trades away the visitor's filter state to guarantee the
        // link they followed always resolves to something visible. Replaced in the history, not
        // pushed: the page did this, not the visitor.
        if (item.hidden) {
            this.clearFilters(FILTER_GROUPS, "replace");
        }

        if (this.openItem(item)) {
            trackCompanyOpen(item);
        }
        scrollWhenStyled(item, { behavior: "smooth", block: "nearest" });
    }

    // A filter can hide the linked section (a Division filter hides the whole division, a location
    // filter can empty a category's grid) - cleared for the same reason as openFromHash's. The
    // browser's own on-load jump to the anchor happens before this runs, while the section may
    // still be hidden, so it's scrolled to here explicitly.
    private scrollToSection(id: string): void {
        const section = [...this.divisions, ...this.categories].find((candidate) => candidate.id === id);
        if (!section) {
            return;
        }

        const division = section.closest<HTMLElement>("[data-company-listing-division]");
        const grid = section.hasAttribute("data-company-listing-category") ? section.querySelector<HTMLElement>("[data-company-listing-grid]") : null;
        if (division?.hidden || grid?.hidden) {
            this.clearFilters(FILTER_GROUPS, "replace");
        }

        scrollWhenStyled(section, { behavior: "smooth", block: "start" });
    }
}

// Blocks initialize once components.css has applied - or, on a slow connection, after
// startup.ts stops waiting for it. Unstyled, none of the panels are collapsed and the page is
// thousands of pixels longer, so a scroll to an anchor then would land far off once the CSS
// arrives: wait for the real thing (immediate when it's already there).
function scrollWhenStyled(element: HTMLElement, options: ScrollIntoViewOptions): void {
    void whenComponentsCssApplied(Infinity).then(() => element.scrollIntoView(options));
}

// Engage "Company/Open": a company panel opened, by a click in the listing or a nav link
// (#<company>, see openFromHash). Called only when toggleItem actually opened the panel - the
// same button closes it too.
function trackCompanyOpen(item: HTMLButtonElement): void {
    const name = item.dataset.companyName;
    if (name) {
        trackEngageEvent({ category: "Company", action: "Open", label: name });
    }
}

// Re-chunks a grid into rows of its column count from the tiles still visible, each row's
// tiles followed by their panels - the order BlockCompanyListing.cshtml renders (see its
// ChunkByRow comment for why panels must follow their own row). Rendered order alone would let a
// filter leave sparse rows: the panels after every group of three end a row, so the tiles left
// in one group couldn't move up into the row before. A hidden tile's panel is hidden too (it
// would otherwise still end a row). The tiles keep their original order.
function arrangeGrid({ element, columns, entries }: Grid): void {
    const visible = entries.filter(({ item }) => !item.hidden);
    const hidden = entries.filter(({ item }) => item.hidden);

    const order: HTMLElement[] = [];
    for (let start = 0; start < visible.length; start += columns) {
        const row = visible.slice(start, start + columns);
        row.forEach(({ item }) => order.push(item));
        row.forEach(({ panel }) => {
            if (panel) {
                panel.hidden = false;
                order.push(panel);
            }
        });
    }
    hidden.forEach(({ item, panel }) => {
        order.push(item);
        if (panel) {
            panel.hidden = true;
            order.push(panel);
        }
    });

    // Moved only when something changed, so an unfiltered page keeps its server-rendered DOM.
    const current = Array.from(element.children).filter((child) => order.includes(child as HTMLElement));
    if (current.length !== order.length || current.some((child, index) => child !== order[index])) {
        order.forEach((child) => element.append(child));
    }
}

// Recomputes the grid-line classes (block-company-listing.css, "Grid lines") from the tiles
// that are actually visible, since a filter hiding tiles makes the rest reflow and the
// server-rendered classes (BlockCompanyListing.cshtml) no longer match:
// - --row-end: the last tile of a full row (no right border - the outer edge is .site-wrapper's);
// - --last-row: tiles in the grid's last row (no bottom border - the grid closes it full width);
// - panel --last-row: a panel after the last row draws its top line instead of its bottom.
// Panels span the whole row (grid-column: 1 / -1), so each one ends the row of tiles before it.
function updateGridLines({ element: grid, columns }: Grid): void {
    const rows: HTMLElement[][] = [];
    const panelsAfterRow = new Map<HTMLElement, number>();
    let current: HTMLElement[] = [];

    Array.from(grid.children).forEach((child) => {
        const element = child as HTMLElement;
        if (element.hidden) {
            return;
        }
        if (element.hasAttribute("data-company-listing-panel")) {
            if (current.length > 0) {
                rows.push(current);
                current = [];
            }
            panelsAfterRow.set(element, rows.length - 1);
            return;
        }
        if (element.hasAttribute("data-company-listing-item")) {
            current.push(element);
            if (current.length === columns) {
                rows.push(current);
                current = [];
            }
        }
    });
    if (current.length > 0) {
        rows.push(current);
    }

    const lastRowIndex = rows.length - 1;
    rows.forEach((row, rowIndex) => {
        row.forEach((item, columnIndex) => {
            item.classList.toggle("block-company-listing__item--row-end", columnIndex === columns - 1);
            item.classList.toggle("block-company-listing__item--last-row", rowIndex === lastRowIndex);
        });
    });
    panelsAfterRow.forEach((rowIndex, panel) => {
        panel.classList.toggle("block-company-listing__panel--last-row", rowIndex === lastRowIndex);
    });
}

export function initBlockCompanyListing(): void {
    document.querySelectorAll<HTMLElement>("[data-company-listing]").forEach((root) => {
        new CompanyListing(root);
    });
}
